import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import {
  CombinedMeasurementsConfig,
  MeasurementsConfig,
} from "../../@types/Measurements";
import { measurementsService } from "../../services/measurementsService";

export function useMeasurementConfigs({
  patientId,
  section,
}: {
  patientId: number | undefined;
  section: string | undefined;
}) {
  const { data, isError, isFetching } = useQuery({
    queryKey: [`${section}-measurement-configs-${patientId}`, patientId],
    queryFn: async () => {
      if (!patientId || !section) return [] as MeasurementsConfig[];

      const { data } = await measurementsService.getPatientMeasurementConfigs(
        patientId,
        section
      );

      return data;
    },
  });

  return {
    configs: (data ?? []) as MeasurementsConfig[],
    isFetching,
    isError,
  };
}

export function useMeasurementTypeConfig({
  measurementType,
}: {
  measurementType: string;
}) {
  const { data, isError, isFetching } = useQuery<MeasurementsConfig>({
    queryKey: [`measurement-type-config-${measurementType}`],
    queryFn: async () => {
      const { data } = await measurementsService.getMeasurementTypeConfig(
        measurementType
      );

      return data;
    },
  });

  return {
    config: data as MeasurementsConfig,
    isFetching,
    isError,
  };
}

/**
 * Fetches combined measurements and lab results configs in a single API call.
 * Used by PatientPage to reduce API calls and enable accurate skeleton rendering.
 */
export function useCombinedMeasurementConfigs({
  patientId,
}: {
  patientId: number | undefined;
}) {
  const { data, isError, isFetching, isLoading } =
    useQuery<CombinedMeasurementsConfig | null>({
      queryKey: ["combined-measurement-configs", patientId],
      queryFn: async () => {
        if (!patientId) return null;

        const { data } =
          await measurementsService.getCombinedPatientMeasurementConfigs(
            patientId
          );

        return data;
      },
      enabled: !!patientId,
    });

  const measurementsConfigs = useMemo(
    () => (data?.measurements ?? []) as MeasurementsConfig[],
    [data?.measurements]
  );

  const labResultsConfigs = useMemo(
    () => (data?.labResults ?? []) as MeasurementsConfig[],
    [data?.labResults]
  );

  return {
    measurementsConfigs,
    labResultsConfigs,
    measurementsCount: data?.measurementsCount ?? 0,
    labResultsCount: data?.labResultsCount ?? 0,
    // EN-5137: the cards were reconfigured since this clinician last saw them. The token identifies
    // the set being shown and is what acknowledges it.
    measurementCardsChanged: data?.measurementCardsChanged ?? false,
    measurementCardsToken: data?.measurementCardsToken ?? null,
    isFetching,
    isLoading,
    isError,
  };
}
